import React from 'react';
import type {ReactFlowInstance} from 'reactflow';

import {getZoomAnimationDurationMs} from 'ui-library/reactflow/ReactFlowConstants';

export interface CenterNodeAxes {
  horizontal: boolean;
  vertical: boolean;
}

export interface CenterNodeParams {
  reactFlowInstance: ReactFlowInstance;
  reactFlowWrapper: React.RefObject<HTMLDivElement>;
  leftSidepanelRef: React.RefObject<HTMLDivElement>;
  rightSidepanelRef: React.RefObject<HTMLDivElement>;
  nodePosition: {x: number; y: number};
  nodeWidth?: number;
  nodeHeight?: number;
  /**
   * Horizontal gap (in pixels) between the wrapper's left edge and the left edge of the ReactFlow
   * canvas (i.e. the canvas's left margin within the wrapper). Defaults to 0 (canvas flush with the
   * wrapper). Consumers whose canvas is inset should pass their inset — e.g. the process flowchart
   * config editor passes its icon-sidebar width, and the study visualization passes its overview
   * panel width when that panel is open.
   */
  canvasLeftMargin?: number;
  /** Which axes to move. Defaults to both. */
  axes?: CenterNodeAxes;
}

interface NodeCenter {
  x: number;
  y: number;
}

const RIGHT_SIDEPANEL_FULL_WIDTH_PERCENTAGE = 0.9;
const DEFAULT_AXES: CenterNodeAxes = {horizontal: true, vertical: true};

function calculateNodeCenter(
  nodePosition: {x: number; y: number},
  nodeWidth?: number,
  nodeHeight?: number
): NodeCenter {
  return {
    x: nodeWidth != null ? nodePosition.x + nodeWidth / 2 : nodePosition.x,
    y: nodeHeight != null ? nodePosition.y + nodeHeight / 2 : nodePosition.y,
  };
}

function getPanelWidth(panelRef: React.RefObject<HTMLDivElement>): number {
  const panelElement = panelRef.current;
  return panelElement ? panelElement.getBoundingClientRect().width : 0;
}

interface CenterNodeAtScreenPositionParams {
  reactFlowInstance: ReactFlowInstance;
  targetCenterXScreen: number;
  targetCenterYScreen: number;
  wrapperRect: DOMRect;
  nodeCenter: NodeCenter;
  canvasLeftMargin: number;
  axes: CenterNodeAxes;
}

function centerNodeAtScreenPosition({
  reactFlowInstance,
  targetCenterXScreen,
  targetCenterYScreen,
  wrapperRect,
  nodeCenter,
  canvasLeftMargin,
  axes,
}: CenterNodeAtScreenPositionParams): void {
  const currentViewport = reactFlowInstance.getViewport();
  const {zoom} = currentViewport;

  const targetXRelativeToWrapper = targetCenterXScreen - wrapperRect.left - canvasLeftMargin;
  const targetYRelativeToWrapper = targetCenterYScreen - wrapperRect.top;

  const nodeScreenX = nodeCenter.x * zoom + currentViewport.x;
  const nodeScreenY = nodeCenter.y * zoom + currentViewport.y;

  const offsetX = axes.horizontal ? targetXRelativeToWrapper - nodeScreenX : 0;
  const offsetY = axes.vertical ? targetYRelativeToWrapper - nodeScreenY : 0;

  if (offsetX === 0 && offsetY === 0) {
    return;
  }

  const newViewportX = currentViewport.x + offsetX;
  const newViewportY = currentViewport.y + offsetY;

  if (!Number.isFinite(newViewportX) || !Number.isFinite(newViewportY)) {
    return;
  }

  reactFlowInstance.setViewport(
    {x: newViewportX, y: newViewportY, zoom},
    {duration: getZoomAnimationDurationMs()}
  );
}

/**
 * Pans (without changing zoom) the ReactFlow viewport so the given node/bounds is centered in the
 * canvas area that remains visible with the left and/or right side panels open. When the right panel
 * takes up (nearly) the whole canvas, the node is centered in the total canvas instead, so it ends
 * up centered once that panel closes.
 *
 * `axes` restricts which axes move. Both axes move by default.
 *
 * Shared across ReactFlow flowcharts (process flowchart config editor, study visualization, etc.).
 * Layouts whose canvas is inset from the wrapper by something other than the icon sidebar should
 * pass {@link CenterNodeParams.canvasLeftMargin}.
 */
export function centerNodeInVisibleCanvas({
  reactFlowInstance,
  reactFlowWrapper,
  leftSidepanelRef,
  rightSidepanelRef,
  nodePosition,
  nodeWidth,
  nodeHeight,
  canvasLeftMargin = 0,
  axes,
}: CenterNodeParams): void {
  const wrapperElement = reactFlowWrapper.current;
  if (wrapperElement == null) {
    return;
  }

  const wrapperRect = wrapperElement.getBoundingClientRect();
  if (wrapperRect.width <= 0 || wrapperRect.height <= 0) {
    return;
  }

  const nodeCenter = calculateNodeCenter(nodePosition, nodeWidth, nodeHeight);

  const leftPanelWidth = getPanelWidth(leftSidepanelRef);
  const rightPanelWidth = getPanelWidth(rightSidepanelRef);

  const effectiveLeftPanelWidth = leftPanelWidth > 0 ? leftPanelWidth : canvasLeftMargin;
  const visibleAreaLeft = wrapperRect.left + effectiveLeftPanelWidth;
  const visibleAreaRight = wrapperRect.right - rightPanelWidth;
  const visibleCanvasWidth = visibleAreaRight - visibleAreaLeft;
  const availableCanvasWidth = wrapperRect.width - effectiveLeftPanelWidth;

  const isRightPanelFullWidth =
    rightPanelWidth > 0 && rightPanelWidth >= availableCanvasWidth * RIGHT_SIDEPANEL_FULL_WIDTH_PERCENTAGE;
  const shouldCenterInTotal = isRightPanelFullWidth || visibleCanvasWidth <= 0;

  const targetCenterYScreen = wrapperRect.top + wrapperRect.height / 2;

  const totalCanvasLeft = wrapperRect.left + effectiveLeftPanelWidth;
  const totalCanvasWidth = wrapperRect.width - effectiveLeftPanelWidth;
  const centerInTotalCanvas = totalCanvasLeft + totalCanvasWidth / 2;
  const centerInVisibleCanvas = visibleAreaLeft + visibleCanvasWidth / 2;

  const targetCenterXScreen = shouldCenterInTotal ? centerInTotalCanvas : centerInVisibleCanvas;

  if (!Number.isFinite(targetCenterXScreen) || !Number.isFinite(targetCenterYScreen)) {
    return;
  }

  centerNodeAtScreenPosition({
    reactFlowInstance,
    targetCenterXScreen,
    targetCenterYScreen,
    wrapperRect,
    nodeCenter,
    canvasLeftMargin,
    axes: axes ?? DEFAULT_AXES,
  });
}
